// packages/api/src/PublicDmService.ts
//
// PUBLIC DIRECT MESSAGES client (roadmap #6, PM epic) — the guest-facing surface for private guest↔guest
// conversations on the event website. Visitor-identified, published-only, gated by eventsmagic-api's
// PublicDmCore (recognized guest + conversation membership + host DM policy + opt-out). Mirrors
// PublicCommentService; messages reuse the guest-safe PublicComment shape.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import type { PublicComment } from "./PublicCommentService";

/** A person the caller can direct-message (autosuggest) — name only. */
export interface DmDirectoryEntry {
  visitorId: string;
  name?: string | null;
}

/** A participant of a DM conversation. */
export interface DmParticipant {
  visitorId: string;
  name?: string | null;
  /** True for the current viewer. */
  isMe?: boolean | null;
}

/** A DM conversation the caller is part of. */
export interface DmConversation {
  id: string;
  participants?: DmParticipant[] | null;
  lastMessageAt?: string | null;
  lastMessageText?: string | null;
  /** True when the caller sent the last message. */
  lastMessageMine?: boolean | null;
}

/** The caller's DM preferences. */
export interface DmPrefs {
  /** True when the guest has opted out of receiving direct messages. */
  optOut?: boolean | null;
}

// Messages reuse the guest-safe comment shape.
const DM_MESSAGE_FIELDS = `
  id
  channelCommentId
  commenterName
  commenterKind
  commentText
  hasReplies
  createdAt
  reactions { reaction count mine }
  mine
  editable
`;

const CONVERSATION_FIELDS = `
  id
  participants { visitorId name isMe }
  lastMessageAt
  lastMessageText
  lastMessageMine
`;

const DM_DIRECTORY_QUERY = gql`
  query PublicDmDirectory($url: String!, $visitorId: ID, $query: String) {
    publicDmDirectory(url: $url, visitorId: $visitorId, query: $query) {
      code
      errors { name errors warnings }
      data { visitorId name }
    }
  }
`;

const DM_LIST_QUERY = gql`
  query PublicDmList($url: String!, $visitorId: ID) {
    publicDmList(url: $url, visitorId: $visitorId) {
      code
      errors { name errors warnings }
      data { ${CONVERSATION_FIELDS} }
    }
  }
`;

const DM_THREAD_QUERY = gql`
  query PublicDmThread($url: String!, $visitorId: ID, $conversationId: ID!, $page: Int, $pageSize: Int) {
    publicDmThread(url: $url, visitorId: $visitorId, conversationId: $conversationId, page: $page, pageSize: $pageSize) {
      code
      errors { name errors warnings }
      data { ${DM_MESSAGE_FIELDS} }
    }
  }
`;

// #128/#133 — "Load older": a SEPARATE document, so the newest-page read above never depends on the cursor arguments —
// a client deployed before the gateway recomposes keeps every thread readable (only Load older waits for it).
const DM_THREAD_OLDER_QUERY = gql`
  query PublicDmThreadOlder($url: String!, $visitorId: ID, $conversationId: ID!, $page: Int, $pageSize: Int, $beforeId: ID!, $before: String) {
    publicDmThread(url: $url, visitorId: $visitorId, conversationId: $conversationId, page: $page, pageSize: $pageSize, beforeId: $beforeId, before: $before) {
      code
      errors { name errors warnings }
      data { ${DM_MESSAGE_FIELDS} }
    }
  }
`;

const DM_GET_PREFS_QUERY = gql`
  query PublicDmGetPrefs($url: String!, $visitorId: ID) {
    publicDmGetPrefs(url: $url, visitorId: $visitorId) {
      code
      errors { name errors warnings }
      data { optOut }
    }
  }
`;

const DM_START_MUTATION = gql`
  mutation PublicDmStart($url: String!, $visitorId: ID, $recipientVisitorIds: [ID]) {
    publicDmStart(url: $url, visitorId: $visitorId, recipientVisitorIds: $recipientVisitorIds) {
      code
      errors { name errors warnings }
      data { ${CONVERSATION_FIELDS} }
    }
  }
`;

const DM_POST_MUTATION = gql`
  mutation PublicDmPost($url: String!, $visitorId: ID, $conversationId: ID!, $commentText: String!) {
    publicDmPost(url: $url, visitorId: $visitorId, conversationId: $conversationId, commentText: $commentText) {
      code
      errors { name errors warnings }
      data { ${DM_MESSAGE_FIELDS} }
    }
  }
`;

const DM_SET_OPT_OUT_MUTATION = gql`
  mutation PublicDmSetOptOut($url: String!, $visitorId: ID, $optOut: Boolean!) {
    publicDmSetOptOut(url: $url, visitorId: $visitorId, optOut: $optOut) {
      code
      errors { name errors warnings }
      data { optOut }
    }
  }
`;

const DM_REACT_MUTATION = gql`
  mutation PublicDmReact($url: String!, $visitorId: ID, $conversationId: ID!, $channelCommentId: ID!, $reaction: String!) {
    publicDmReact(url: $url, visitorId: $visitorId, conversationId: $conversationId, channelCommentId: $channelCommentId, reaction: $reaction) {
      code
      errors { name errors warnings }
      data { id reactions { reaction count mine } }
    }
  }
`;

export class PublicDmService {
  /** Autosuggest of guests the caller may direct-message (recognized guests only; empty when DMs are off). */
  static async directory(url: string, visitorId?: string | null, query?: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(
      DM_DIRECTORY_QUERY,
      { url, visitorId: visitorId ?? null, query: query ?? null },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /** The caller's DM conversations on an event (newest activity first). */
  static async list(url: string, visitorId?: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(DM_LIST_QUERY, { url, visitorId: visitorId ?? null }, { fetchPolicy: "network-only", ...options });
  }

  /**
   * Read a conversation's messages (must be a participant). Newest first. `before` (#128) — the createdAt of the oldest
   * message on screen — reads the page older than it; that message itself may come back again (drop it by id).
   */
  static async thread(
    args: {
      url: string;
      visitorId?: string | null;
      conversationId: string;
      page?: number | null;
      pageSize?: number | null;
      beforeId?: string | null;
      before?: string | null;
    },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return ApiService.executeQuery(
      args.beforeId ? DM_THREAD_OLDER_QUERY : DM_THREAD_QUERY,
      {
        url: args.url,
        visitorId: args.visitorId ?? null,
        conversationId: args.conversationId,
        page: args.page ?? null,
        pageSize: args.pageSize ?? null,
        ...(args.beforeId ? { beforeId: args.beforeId, before: args.before ?? null } : {}),
      },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /** The caller's own DM opt-out preference. */
  static async getPrefs(url: string, visitorId?: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeQuery(DM_GET_PREFS_QUERY, { url, visitorId: visitorId ?? null }, { fetchPolicy: "network-only", ...options });
  }

  /** Start (or reopen) a conversation with one or more recipients (idempotent; recipients validated server-side). */
  static async start(url: string, visitorId: string | null | undefined, recipientVisitorIds: string[], options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(
      DM_START_MUTATION,
      { url, visitorId: visitorId ?? null, recipientVisitorIds },
      options
    );
  }

  /** Send a message into a conversation the caller is part of. */
  static async post(args: { url: string; visitorId?: string | null; conversationId: string; commentText: string }, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(
      DM_POST_MUTATION,
      { url: args.url, visitorId: args.visitorId ?? null, conversationId: args.conversationId, commentText: args.commentText },
      options
    );
  }

  /** Set the caller's own DM opt-out (true = don't receive direct messages). */
  static async setOptOut(url: string, visitorId: string | null | undefined, optOut: boolean, options: Record<string, any> = {}): Promise<OpResult> {
    return ApiService.executeMutation(DM_SET_OPT_OUT_MUTATION, { url, visitorId: visitorId ?? null, optOut }, options);
  }

  /** Toggle a reaction on a message in a conversation the caller is part of; returns the message's fresh summary. */
  static async react(
    args: { url: string; visitorId?: string | null; conversationId: string; channelCommentId: string; reaction: string },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return ApiService.executeMutation(
      DM_REACT_MUTATION,
      { url: args.url, visitorId: args.visitorId ?? null, conversationId: args.conversationId, channelCommentId: args.channelCommentId, reaction: args.reaction },
      options
    );
  }
}

export type { PublicComment };
